// Cloudflare Turnstile — explicit-рендер поверх window.turnstile (грузится
// хуком useTurnstile). appearance: 'interaction-only' — для живого человека
// в 99% случаев форма визуально не меняется вообще (TZ_turnstile_captcha.md, 4.2).
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTurnstile } from '../hooks/useTurnstile'

export interface TurnstileWidgetHandle {
	/** Токен одноразовый — звать после ЛЮБОЙ неудачной отправки формы,
	 *  иначе вторая попытка поймает timeout-or-duplicate. */
	reset: () => void
}

interface TurnstileWidgetProps {
	action: string
	onToken: (token: string) => void
	onExpire?: () => void
	/** Проверка не прошла (сеть, Cloudflare, чужой домен): форма, которая ждёт токен, перестаёт ждать сразу. */
	onError?: () => void
	className?: string
}

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
	function TurnstileWidget({ action, onToken, onExpire, onError, className }, ref) {
		const { t, i18n } = useTranslation()
		const { ready, sitekey } = useTurnstile()
		const containerRef = useRef<HTMLDivElement>(null)
		const widgetIdRef = useRef<string | null>(null)
		const [widgetError, setWidgetError] = useState(false)

		useImperativeHandle(ref, () => ({
			reset: () => {
				if (widgetIdRef.current && window.turnstile) {
					window.turnstile.reset(widgetIdRef.current)
				}
				setWidgetError(false)
			},
		}), [])

		useEffect(() => {
			if (!ready || !sitekey || !containerRef.current || !window.turnstile) return
			const turnstile = window.turnstile

			const id = turnstile.render(containerRef.current, {
				sitekey,
				action,
				theme: 'auto',
				language: i18n.language || 'ru',
				appearance: 'interaction-only',
				callback: (token: string) => {
					setWidgetError(false)
					onToken(token)
				},
				'expired-callback': () => { onExpire?.() },
				'error-callback': () => {
					setWidgetError(true)
					onError?.()
				},
			})
			widgetIdRef.current = id

			return () => {
				if (widgetIdRef.current) {
					turnstile.remove(widgetIdRef.current)
					widgetIdRef.current = null
				}
			}
			// action/sitekey/ready — единственное, от чего реально должен
			// пере-рендериться виджет; onToken/onExpire — стабильные колбэки формы.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [ready, sitekey, action])

		if (!sitekey) return null

		if (!ready) {
			// Зарезервированное место — форма не «прыгает», пока грузится скрипт CF.
			return <div className={`turnstile-widget turnstile-widget--loading ${className || ''}`}>{t('auth.captcha.loading')}</div>
		}

		return (
			<div className={`turnstile-widget ${className || ''}`}>
				<div ref={containerRef} />
				{widgetError && <div className="auth-field-error">{t('auth.captcha.failed')}</div>}
			</div>
		)
	}
)

export default TurnstileWidget
